import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';
import { HashRouter, Navigate, Route, Routes } from 'react-router';
import { Layout } from './components/Layout';
import { ErrorBox, Loading, ToastProvider } from './components/ui';
import { FilePreviewHost } from './components/FilePreview';
import { setLanguage } from './i18n';
import { onDemoPersonChange } from './lib/demo-state';
import { useQ } from './lib/hooks';
import { MeProvider } from './lib/me';
import { clearStickyState } from './lib/sticky';
import { authCall, RpcError, setStepUpHandler, setUnauthenticatedHandler } from './lib/rpc';
import { LoginPage, SetupPage, TotpSetupPage } from './pages/Auth';
import { MyAbsencesPage } from './pages/MyAbsences';
import { TeamPage } from './pages/Team';
import { AbsencesPage } from './pages/Absences';
import { AnnouncementsPage } from './pages/Announcements';
import { AdminPage } from './pages/Admin';
import { AnalysisPage } from './pages/Analysis';
import { ApprovalsPage } from './pages/Approvals';
import { LeaveRequestsPage } from './pages/LeaveRequests';
import { BillingDocPage, BillingPage } from './pages/Billing';
import { CompanyPage } from './pages/Company';
import { requestStepUp, StepUpGate, StepUpHost, stepUpGateActive, type StepUpArea } from './components/step-up';
import { CustomersPage } from './pages/Customers';
import { EntriesPage } from './pages/Entries';
import { CockpitPage } from './pages/Cockpit';
import { EvaluationPage } from './pages/Evaluation';
import { HelpPage } from './pages/Help';
import { OverviewPage } from './pages/Overview';
import { PeoplePage, PersonPage } from './pages/People';
import { PlanningPage } from './pages/Planning';
import { ProfilePage } from './pages/Profile';
import { ProjectPage, ProjectsPage } from './pages/Projects';
import { TasksPage } from './pages/Tasks';
import { WeekPage } from './pages/Week';
import { WorkloadPage } from './pages/Workload';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      retry: (count, error) => count < 1 && !(error instanceof RpcError),
    },
  },
});

// a call that needs the additional confirmation (time over, or money outside the modules, D-04): the gate of a module
// page asks for it itself; elsewhere the confirmation dialog opens (request 31 of round 4, 41 of round 5)
setStepUpHandler((details) => {
  void queryClient.invalidateQueries({ predicate: (q) => q.queryKey[0] === 'me.stepUp' });
  if (!stepUpGateActive()) requestStepUp(((details as { area?: StepUpArea } | undefined)?.area ?? 'finance') as StepUpArea);
});

export function App() {
  // demo: switching the persona remounts the whole app with an empty cache (new permissions everywhere)
  const [demoKey, setDemoKey] = useState(0);
  useEffect(() => {
    if (!__DEMO__) return;
    return onDemoPersonChange(() => {
      queryClient.clear();
      clearStickyState();
      window.location.hash = '#/';
      setDemoKey((k) => k + 1);
    });
  }, []);
  return (
    <QueryClientProvider client={queryClient}>
      <ToastProvider>
        <HashRouter>{__DEMO__ ? <AuthedApp key={demoKey} /> : <SessionGate />}</HashRouter>
        {__DEMO__ && <FilePreviewHost />}
      </ToastProvider>
    </QueryClientProvider>
  );
}

interface SessionInfo {
  authenticated: boolean;
  mfaRequired?: boolean;
  mfaPassed?: boolean;
  entraEnabled: boolean;
  language?: string;
}

function SessionGate() {
  const [session, setSession] = useState<SessionInfo | null>(null);
  const refresh = useCallback(async () => {
    try {
      const s = await authCall<SessionInfo>('session', undefined, 'GET');
      setSession(s);
    } catch {
      setSession({ authenticated: false, entraEnabled: false });
    }
  }, []);
  useEffect(() => {
    void refresh();
    setUnauthenticatedHandler(() => {
      queryClient.clear();
      void refresh();
    });
  }, [refresh]);
  if (!session) return <Loading />;
  if (!session.authenticated) {
    return (
      <Routes>
        <Route path="/setup/:token" element={<SetupPage onDone={refresh} />} />
        <Route path="*" element={<LoginPage entraEnabled={session.entraEnabled} onDone={refresh} />} />
      </Routes>
    );
  }
  if (session.mfaRequired && !session.mfaPassed) return <TotpSetupPage onDone={refresh} />;
  return <AuthedApp />;
}

function AuthedApp() {
  const me = useQ('me.get', {});
  useEffect(() => {
    if (!me.data) return;
    let saved: string | null = null;
    try {
      saved = localStorage.getItem('pz-lang');
    } catch {
      /* storage not available */
    }
    if (!saved && !__DEMO__) setLanguage(me.data.person.language as 'de');
  }, [me.data]);
  if (me.isLoading) return <Loading />;
  if (me.error || !me.data) return <div className="p-6"><ErrorBox error={me.error} /></div>;
  return (
    <MeProvider me={me.data}>
      <Routes>
        <Route element={<Layout />}>
          <Route index element={<OverviewPage />} />
          <Route path="week" element={<WeekPage />} />
          <Route path="entries" element={<EntriesPage />} />
          <Route path="tasks" element={<TasksPage />} />
          <Route path="approvals" element={<ApprovalsPage />} />
          <Route path="leave-requests" element={<LeaveRequestsPage />} />
          <Route path="analysis" element={<AnalysisPage />} />
          <Route
            path="billing"
            element={
              <StepUpGate area="billing">
                <BillingPage />
              </StepUpGate>
            }
          />
          <Route
            path="billing/:id"
            element={
              <StepUpGate area="billing">
                <BillingDocPage />
              </StepUpGate>
            }
          />
          <Route path="planning" element={<PlanningPage />} />
          <Route path="workload" element={<WorkloadPage />} />
          <Route path="cockpit" element={<CockpitPage />} />
          <Route path="evaluation" element={<EvaluationPage />} />
          <Route path="absences" element={<AbsencesPage />} />
          <Route path="my-absences" element={<MyAbsencesPage />} />
          <Route path="team" element={<TeamPage />} />
          <Route path="kpi" element={<Navigate to="/cockpit" replace />} />
          <Route path="help" element={<HelpPage />} />
          <Route path="projects" element={<ProjectsPage />} />
          <Route path="projects/:id" element={<ProjectPage />} />
          <Route path="people" element={<PeoplePage />} />
          <Route path="people/:id" element={<PersonPage />} />
          <Route path="admin" element={<AdminPage />} />
          <Route path="announcements" element={<AnnouncementsPage />} />
          <Route
            path="customers"
            element={
              <StepUpGate area="customers">
                <CustomersPage />
              </StepUpGate>
            }
          />
          <Route
            path="customers/:id"
            element={
              <StepUpGate area="customers">
                <CompanyPage />
              </StepUpGate>
            }
          />
          <Route path="profile" element={<ProfilePage />} />
          <Route path="*" element={<OverviewPage />} />
        </Route>
      </Routes>
      <StepUpHost />
    </MeProvider>
  );
}
